import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Mic, Square, Pause, Play, Loader2, FileText, Download, Trash2, RefreshCw,
  Monitor, Smartphone, Clock, User, CheckCircle2, AlertTriangle, ListChecks, Eye,
  Video, Upload, FileAudio, Pencil, Plus, X, Search, MicOff
} from "lucide-react";

const SEG_MS = 5 * 60 * 1000;

const ESTADO_LABEL = {
  grabando: { label: "Grabando", cls: "bg-red-100 text-red-700 border-red-200" },
  subiendo: { label: "Subiendo archivo", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  convirtiendo: { label: "Convirtiendo audio", cls: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  procesando: { label: "Procesando", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  transcribiendo: { label: "Transcribiendo", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  identificando_hablantes: { label: "Identificando hablantes", cls: "bg-violet-100 text-violet-700 border-violet-200" },
  generando_minuta: { label: "Generando minuta", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  completada: { label: "Completada", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  error: { label: "Error", cls: "bg-red-100 text-red-700 border-red-200" },
};

const EN_PROCESO = ["convirtiendo", "procesando", "transcribiendo", "identificando_hablantes", "generando_minuta"];

const ORIGEN_INFO = {
  movil: { Icon: Smartphone, label: "Celular" },
  pc: { Icon: Monitor, label: "PC" },
  virtual: { Icon: Video, label: "Reunión Virtual" },
  importada: { Icon: FileAudio, label: "Importada" },
};

const fmtTiempo = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  const p = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${p(h)}:${p(m)}:${p(ss)}` : `${p(m)}:${p(ss)}`;
};

const fmtFecha = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("es-DO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 16).replace("T", " "); }
};

const pickMime = () => {
  const opciones = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
  for (const m of opciones) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
};

export default function GrabacionesPage() {
  const { user, getAuthHeaders } = useAuth();
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 768px)").matches);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Grabación
  const [recState, setRecState] = useState("idle"); // idle | grabando | pausado | finalizando
  const [elapsed, setElapsed] = useState(0);
  const [showNuevaDialog, setShowNuevaDialog] = useState(false);
  const [tituloNueva, setTituloNueva] = useState("");
  const [modoGrabacion, setModoGrabacion] = useState("mic"); // mic | virtual
  const [pendingUploads, setPendingUploads] = useState(0);

  // Subir grabación existente (Teams/Zoom/Meet)
  const [showSubirDialog, setShowSubirDialog] = useState(false);
  const [archivoSubir, setArchivoSubir] = useState(null);
  const [tituloSubir, setTituloSubir] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);

  // Detalle
  const [detalle, setDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioIdx, setAudioIdx] = useState(0);
  const [audioLoading, setAudioLoading] = useState(false);

  // Edición de minuta
  const [editOpen, setEditOpen] = useState(false);
  const [editAcuerdos, setEditAcuerdos] = useState([]);
  const [editTareas, setEditTareas] = useState([]);
  const [editHablantes, setEditHablantes] = useState([]);
  const [guardandoMinuta, setGuardandoMinuta] = useState(false);

  // Buscador
  const [busqueda, setBusqueda] = useState("");
  const [resultados, setResultados] = useState(null);
  const [buscando, setBuscando] = useState(false);

  // Ayuda de permiso de micrófono
  const [micAyudaOpen, setMicAyudaOpen] = useState(false);
  const [micErrorTipo, setMicErrorTipo] = useState("denegado"); // denegado | sinmic | ocupado

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const grabacionIdRef = useRef(null);
  const segIndexRef = useRef(0);
  const mimeRef = useRef("");
  const recordingRef = useRef(false);
  const timerRef = useRef(null);
  const elapsedRef = useRef(0);
  const uploadChainRef = useRef(Promise.resolve());
  const segTimerRef = useRef(null);
  const canvasRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const audioElRef = useRef(null);
  const wakeLockRef = useRef(null);
  const finalizandoRef = useRef(false);
  const detenerRef = useRef(null);
  const mixCtxRef = useRef(null);
  const extraStreamsRef = useRef([]);

  // Mantener la pantalla encendida durante la grabación (Wake Lock)
  const pedirWakeLock = useCallback(async () => {
    try {
      if ("wakeLock" in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch { /* no soportado o denegado */ }
  }, []);

  const soltarWakeLock = () => {
    try { wakeLockRef.current?.release(); } catch {}
    wakeLockRef.current = null;
  };

  // Al volver a la app (cambio de pestaña/app), re-adquirir el wake lock
  useEffect(() => {
    const fn = () => {
      if (document.visibilityState === "visible" && recordingRef.current) pedirWakeLock();
    };
    document.addEventListener("visibilitychange", fn);
    return () => document.removeEventListener("visibilitychange", fn);
  }, [pedirWakeLock]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const fn = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  const fetchItems = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await axios.get(`${API}/grabaciones`, getAuthHeaders());
      setItems(res.data || []);
    } catch (e) {
      if (!silent) toast.error("Error cargando grabaciones");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Polling mientras hay grabaciones en proceso
  useEffect(() => {
    if (!items.some((i) => EN_PROCESO.includes(i.estado))) return;
    const t = setInterval(() => fetchItems(true), 8000);
    return () => clearInterval(t);
  }, [items, fetchItems]);

  // ====== Visualizador ======
  const startVisualizer = (stream) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const draw = () => {
        const canvas = canvasRef.current;
        if (canvas) {
          const c = canvas.getContext("2d");
          analyser.getByteFrequencyData(data);
          c.clearRect(0, 0, canvas.width, canvas.height);
          const barW = canvas.width / data.length;
          for (let i = 0; i < data.length; i++) {
            const h = Math.max(3, (data[i] / 255) * canvas.height);
            c.fillStyle = recordingRef.current ? "#ef4444" : "#94a3b8";
            c.beginPath();
            c.roundRect(i * barW + 1, (canvas.height - h) / 2, barW - 2, h, 2);
            c.fill();
          }
        }
        rafRef.current = requestAnimationFrame(draw);
      };
      draw();
    } catch { /* visualizador opcional */ }
  };

  const stopVisualizer = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} audioCtxRef.current = null; }
  };

  // ====== Subida de segmentos ======
  const encolarSegmento = useCallback((blob, index) => {
    if (!blob || blob.size === 0) return;
    const gid = grabacionIdRef.current;
    setPendingUploads((p) => p + 1);
    uploadChainRef.current = uploadChainRef.current.then(async () => {
      for (let intento = 1; intento <= 3; intento++) {
        try {
          const fd = new FormData();
          fd.append("index", String(index));
          fd.append("mime_type", mimeRef.current || blob.type || "audio/webm");
          fd.append("file", blob, `seg_${index}`);
          await axios.post(`${API}/grabaciones/${gid}/segmento`, fd, getAuthHeaders());
          break;
        } catch (e) {
          if (intento === 3) toast.error(`No se pudo subir parte ${index + 1} del audio`);
          else await new Promise((r) => setTimeout(r, 1500 * intento));
        }
      }
      setPendingUploads((p) => Math.max(0, p - 1));
    });
  }, [getAuthHeaders]);

  const startSegmentRecorder = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    const opts = { audioBitsPerSecond: 48000 };
    if (mimeRef.current) opts.mimeType = mimeRef.current;
    const rec = new MediaRecorder(stream, opts);
    recorderRef.current = rec;
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    rec.onstop = () => {
      const idx = segIndexRef.current;
      segIndexRef.current += 1;
      encolarSegmento(new Blob(chunks, { type: mimeRef.current || "audio/webm" }), idx);
      if (recordingRef.current) startSegmentRecorder();
    };
    rec.onerror = () => {
      if (recordingRef.current && !finalizandoRef.current) {
        toast.warning("La grabación se interrumpió; guardando lo grabado automáticamente...");
        detenerRef.current?.();
      }
    };
    // timeslice 30s: si el sistema interrumpe la grabación, se pierde máx. ~30s
    rec.start(30000);
    clearTimeout(segTimerRef.current);
    segTimerRef.current = setTimeout(() => {
      if (recordingRef.current && recorderRef.current?.state === "recording") recorderRef.current.stop();
    }, SEG_MS);
  }, [encolarSegmento]);

  const startTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
    }, 1000);
  };

  const iniciarGrabacion = async () => {
    const esVirtual = modoGrabacion === "virtual" && !isMobile;
    try {
      let stream;
      if (esVirtual) {
        // Reunión virtual: capturar audio de la pestaña/pantalla + micrófono, mezclados
        const disp = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        if (disp.getAudioTracks().length === 0) {
          disp.getTracks().forEach((t) => t.stop());
          toast.error('No se compartió el audio. Al elegir la pestaña o pantalla, marca la casilla "Compartir audio" e intenta de nuevo.');
          return;
        }
        let mic = null;
        try {
          mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
        } catch { toast.warning("Sin acceso al micrófono: se grabará solo el audio de la reunión."); }
        const mctx = new (window.AudioContext || window.webkitAudioContext)();
        const dest = mctx.createMediaStreamDestination();
        mctx.createMediaStreamSource(new MediaStream(disp.getAudioTracks())).connect(dest);
        if (mic) mctx.createMediaStreamSource(mic).connect(dest);
        mixCtxRef.current = mctx;
        extraStreamsRef.current = [disp, mic].filter(Boolean);
        const vTrack = disp.getVideoTracks()[0];
        if (vTrack) vTrack.onended = () => {
          if (recordingRef.current && !finalizandoRef.current) {
            toast.warning("Se detuvo el compartir pantalla; guardando la reunión automáticamente...");
            detenerRef.current?.();
          }
        };
        stream = dest.stream;
      } else {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
      }
      streamRef.current = stream;
      // Si el sistema corta el micrófono (bloqueo prolongado, otra app), guardar lo grabado
      const track = stream.getAudioTracks()[0];
      if (track) track.onended = () => {
        if (recordingRef.current && !finalizandoRef.current) {
          toast.warning("El micrófono se interrumpió; guardando la grabación automáticamente...");
          detenerRef.current?.();
        }
      };
      mimeRef.current = pickMime();
      const res = await axios.post(`${API}/grabaciones`, {
        titulo: tituloNueva.trim() || null,
        origen: esVirtual ? "virtual" : (isMobile ? "movil" : "pc"),
      }, getAuthHeaders());
      grabacionIdRef.current = res.data.id;
      segIndexRef.current = 0;
      elapsedRef.current = 0;
      setElapsed(0);
      recordingRef.current = true;
      finalizandoRef.current = false;
      setShowNuevaDialog(false);
      setRecState("grabando");
      startSegmentRecorder();
      startTimer();
      startVisualizer(stream);
      pedirWakeLock();
      toast.success("Grabación iniciada");
    } catch (e) {
      limpiarRecursos();
      if (esVirtual && (e?.name === "NotAllowedError" || e?.name === "AbortError")) {
        toast.error("Se canceló el compartir pantalla.");
        return;
      }
      const nombre = e?.name || "";
      if (["NotFoundError", "DevicesNotFoundError", "OverconstrainedError"].includes(nombre)) setMicErrorTipo("sinmic");
      else if (["NotReadableError", "TrackStartError", "AbortError"].includes(nombre)) setMicErrorTipo("ocupado");
      else setMicErrorTipo("denegado");
      setMicAyudaOpen(true);
    }
  };

  const pausarReanudar = () => {
    const rec = recorderRef.current;
    if (!rec) return;
    if (recState === "grabando") {
      rec.pause();
      clearInterval(timerRef.current);
      clearTimeout(segTimerRef.current);
      setRecState("pausado");
    } else if (recState === "pausado") {
      rec.resume();
      startTimer();
      segTimerRef.current = setTimeout(() => {
        if (recordingRef.current && recorderRef.current?.state === "recording") recorderRef.current.stop();
      }, SEG_MS);
      setRecState("grabando");
    }
  };

  const limpiarRecursos = () => {
    clearInterval(timerRef.current);
    clearTimeout(segTimerRef.current);
    stopVisualizer();
    soltarWakeLock();
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    extraStreamsRef.current.forEach((s) => { try { s.getTracks().forEach((t) => t.stop()); } catch {} });
    extraStreamsRef.current = [];
    if (mixCtxRef.current) { try { mixCtxRef.current.close(); } catch {} mixCtxRef.current = null; }
    recorderRef.current = null;
  };

  const detenerGrabacion = async () => {
    if (finalizandoRef.current) return;
    finalizandoRef.current = true;
    setRecState("finalizando");
    recordingRef.current = false;
    clearInterval(timerRef.current);
    clearTimeout(segTimerRef.current);
    const rec = recorderRef.current;
    const esperaStop = new Promise((resolve) => {
      if (!rec || rec.state === "inactive") return resolve();
      const prev = rec.onstop;
      rec.onstop = (e) => { if (prev) prev(e); resolve(); };
      try { rec.stop(); } catch { resolve(); }
    });
    await esperaStop;
    await uploadChainRef.current;
    try {
      await axios.post(`${API}/grabaciones/${grabacionIdRef.current}/finalizar`, {
        duracion_segundos: elapsedRef.current,
      }, getAuthHeaders());
      toast.success("Grabación guardada. Transcribiendo y generando minuta...");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error finalizando la grabación");
    }
    limpiarRecursos();
    setRecState("idle");
    setTituloNueva("");
    fetchItems(true);
  };
  detenerRef.current = detenerGrabacion;

  // ====== Subir grabación existente ======
  const CHUNK_SIZE = 5 * 1024 * 1024;
  const subirArchivo = async () => {
    if (!archivoSubir) return;
    if (archivoSubir.size > 2 * 1024 * 1024 * 1024) {
      toast.error("El archivo supera los 2 GB");
      return;
    }
    const total = Math.ceil(archivoSubir.size / CHUNK_SIZE);
    try {
      setSubiendoArchivo(true);
      setUploadPct(0);
      const res = await axios.post(`${API}/grabaciones/importar/iniciar`, {
        titulo: tituloSubir.trim() || null,
        filename: archivoSubir.name,
        total_chunks: total,
      }, getAuthHeaders());
      const gid = res.data.id;
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("index", String(i));
        fd.append("file", archivoSubir.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE), `chunk_${i}`);
        await axios.post(`${API}/grabaciones/importar/${gid}/chunk`, fd, getAuthHeaders());
        setUploadPct(Math.round(((i + 1) / total) * 100));
      }
      await axios.post(`${API}/grabaciones/importar/${gid}/completar`, {}, getAuthHeaders());
      toast.success("Archivo subido. Convirtiendo, transcribiendo y generando minuta...");
      setShowSubirDialog(false);
      setArchivoSubir(null);
      setTituloSubir("");
      fetchItems(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo el archivo");
    } finally {
      setSubiendoArchivo(false);
    }
  };

  useEffect(() => () => { recordingRef.current = false; limpiarRecursos(); }, []);

  // ====== Detalle / acciones ======
  const abrirDetalle = async (id) => {
    try {
      setDetalleLoading(true);
      setAudioUrl(null); setAudioIdx(0);
      const res = await axios.get(`${API}/grabaciones/${id}`, getAuthHeaders());
      setDetalle(res.data);
    } catch { toast.error("Error cargando detalle"); }
    finally { setDetalleLoading(false); }
  };

  const cargarAudio = async (idx) => {
    if (!detalle) return;
    try {
      setAudioLoading(true);
      const res = await axios.get(`${API}/grabaciones/${detalle.id}/audio/${idx}`, { ...getAuthHeaders(), responseType: "blob" });
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      const url = URL.createObjectURL(res.data);
      setAudioUrl(url); setAudioIdx(idx);
      setTimeout(() => { audioElRef.current?.play().catch(() => {}); }, 150);
    } catch { toast.error("Error cargando audio"); }
    finally { setAudioLoading(false); }
  };

  const onAudioEnded = () => {
    if (detalle && audioIdx + 1 < (detalle.segmentos || 0)) cargarAudio(audioIdx + 1);
  };

  const descargarPdf = async (g) => {
    try {
      const res = await axios.get(`${API}/grabaciones/${g.id}/pdf`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Minuta_${(g.titulo || "reunion").slice(0, 40).replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("La minuta aún no está disponible"); }
  };

  const eliminar = async (g) => {
    if (!window.confirm(`¿Eliminar la grabación "${g.titulo}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/grabaciones/${g.id}`, getAuthHeaders());
      toast.success("Grabación eliminada");
      if (detalle?.id === g.id) setDetalle(null);
      fetchItems(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };

  const reprocesar = async (g) => {
    try {
      await axios.post(`${API}/grabaciones/${g.id}/reprocesar`, {}, getAuthHeaders());
      toast.success("Reprocesando grabación...");
      fetchItems(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo reprocesar"); }
  };

  // ====== Buscador ======
  const ejecutarBusqueda = async () => {
    const q = busqueda.trim();
    if (q.length < 2) { toast.error("Escribe al menos 2 caracteres"); return; }
    try {
      setBuscando(true);
      const res = await axios.get(`${API}/grabaciones/buscar`, { ...getAuthHeaders(), params: { q } });
      setResultados(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en la búsqueda");
    } finally {
      setBuscando(false);
    }
  };

  const limpiarBusqueda = () => { setBusqueda(""); setResultados(null); };

  const resaltarTexto = (texto, marca) => {
    if (!marca) return texto;
    const idx = texto.toLowerCase().indexOf(marca.toLowerCase());
    if (idx < 0) return texto;
    return (
      <>
        {texto.slice(0, idx)}
        <mark className="bg-yellow-200 dark:bg-yellow-700/60 rounded px-0.5">{texto.slice(idx, idx + marca.length)}</mark>
        {texto.slice(idx + marca.length)}
      </>
    );
  };

  // ====== Edición de minuta ======
  const abrirEditorMinuta = () => {
    const m = detalle?.minuta || {};
    setEditAcuerdos([...(m.acuerdos || [])]);
    setEditTareas((m.tareas || []).map((t) => ({ descripcion: t.descripcion || "", responsable: t.responsable || "" })));
    const unicos = [];
    (detalle?.transcripcion_hablantes || "").split("\n").forEach((l) => {
      const mm = l.trim().match(/^([^:]{1,40}):/);
      if (mm && !unicos.includes(mm[1].trim())) unicos.push(mm[1].trim());
    });
    setEditHablantes(unicos.map((h) => ({ original: h, nuevo: h })));
    setEditOpen(true);
  };

  const guardarMinuta = async () => {
    try {
      setGuardandoMinuta(true);
      const renombres = {};
      editHablantes.forEach((h) => {
        if (h.nuevo.trim() && h.nuevo.trim() !== h.original) renombres[h.original] = h.nuevo.trim();
      });
      await axios.put(`${API}/grabaciones/${detalle.id}/minuta`, {
        acuerdos: editAcuerdos,
        tareas: editTareas,
        renombrar_hablantes: renombres,
      }, getAuthHeaders());
      toast.success("Minuta actualizada");
      setEditOpen(false);
      abrirDetalle(detalle.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la minuta");
    } finally {
      setGuardandoMinuta(false);
    }
  };

  const esAdmin = ["admin", "administrador"].includes((user?.role || "").toLowerCase());
  const grabActiva = recState !== "idle";

  // ====== UI de grabación activa ======
  const controlesGrabacion = (
    <div className="flex items-center gap-3">
      <Button
        data-testid="grabacion-pausar-btn"
        variant="outline"
        size={isMobile ? "lg" : "default"}
        onClick={pausarReanudar}
        disabled={recState === "finalizando"}
        className={isMobile ? "h-16 w-16 rounded-full p-0 border-2" : ""}
      >
        {recState === "pausado" ? <Play className={isMobile ? "h-7 w-7" : "h-4 w-4 mr-1"} /> : <Pause className={isMobile ? "h-7 w-7" : "h-4 w-4 mr-1"} />}
        {!isMobile && (recState === "pausado" ? "Reanudar" : "Pausar")}
      </Button>
      <Button
        data-testid="grabacion-detener-btn"
        size={isMobile ? "lg" : "default"}
        onClick={detenerGrabacion}
        disabled={recState === "finalizando"}
        className={`bg-red-600 hover:bg-red-700 text-white ${isMobile ? "h-20 w-20 rounded-full p-0" : ""}`}
      >
        {recState === "finalizando"
          ? <Loader2 className={`animate-spin ${isMobile ? "h-8 w-8" : "h-4 w-4 mr-1"}`} />
          : <Square className={isMobile ? "h-8 w-8" : "h-4 w-4 mr-1"} fill="currentColor" />}
        {!isMobile && (recState === "finalizando" ? "Guardando..." : "Finalizar")}
      </Button>
    </div>
  );

  const renderMinuta = (m) => (
    <div className="space-y-4 text-sm" data-testid="minuta-contenido">
      <div>
        <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-1">Resumen Ejecutivo</h4>
        {String(m.resumen || "").split("\n").filter(Boolean).map((p, i) => (
          <p key={i} className="mb-2 text-slate-700 dark:text-slate-300">{p}</p>
        ))}
      </div>
      {[
        ["Participantes", m.participantes],
        ["Temas Tratados", m.temas_tratados],
        ["Acuerdos Alcanzados", m.acuerdos],
        ["Puntos Pendientes / No Concretados", m.puntos_pendientes],
        ["Próximos Pasos", m.proximos_pasos],
      ].map(([titulo, lista]) => (lista?.length ? (
        <div key={titulo}>
          <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-1">{titulo}</h4>
          <ul className="list-disc ml-5 space-y-1 text-slate-700 dark:text-slate-300">
            {lista.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
      ) : null))}
      {m.tareas?.length ? (
        <div>
          <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-1">Tareas Asignadas</h4>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-blue-50 dark:bg-slate-800">
                <tr><th className="text-left p-2">Tarea</th><th className="text-left p-2 w-40">Responsable</th></tr>
              </thead>
              <tbody>
                {m.tareas.map((t, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-2">{t.descripcion}</td>
                    <td className="p-2 text-slate-600 dark:text-slate-400">{t.responsable}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950">
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <div className="p-4 md:p-8 max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Mic className="h-7 w-7 text-red-600" /> Grabaciones y Minutas
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Graba reuniones desde {isMobile ? "tu celular" : "tu PC"}, obtén la transcripción y la minuta automática con IA
              </p>
            </div>
            {!grabActiva && (
              <div className="flex items-center gap-2">
                <Button data-testid="subir-grabacion-btn" variant="outline" onClick={() => setShowSubirDialog(true)}>
                  <Upload className="h-4 w-4 mr-2" /> Subir Grabación
                </Button>
                <Button data-testid="nueva-grabacion-btn" onClick={() => { setModoGrabacion("mic"); setShowNuevaDialog(true); }} className="bg-red-600 hover:bg-red-700 text-white">
                  <Mic className="h-4 w-4 mr-2" /> Nueva Grabación
                </Button>
              </div>
            )}
          </div>

          {/* Barra de grabación en PC */}
          {grabActiva && !isMobile && (
            <Card className="mb-6 border-red-200 dark:border-red-900 shadow-md" data-testid="grabacion-activa-pc">
              <CardContent className="p-4 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className={`h-3 w-3 rounded-full ${recState === "grabando" ? "bg-red-600 animate-pulse" : "bg-amber-500"}`} />
                  {modoGrabacion === "virtual" ? <Video className="h-4 w-4 text-slate-400" /> : <Monitor className="h-4 w-4 text-slate-400" />}
                  <span className="font-medium text-slate-700 dark:text-slate-200">
                    {recState === "pausado" ? "Grabación en pausa" : recState === "finalizando" ? "Guardando..." : (modoGrabacion === "virtual" ? "Grabando reunión virtual" : "Grabando reunión")}
                  </span>
                </div>
                <canvas ref={canvasRef} width={220} height={40} className="rounded" />
                <span data-testid="grabacion-timer" className="font-mono text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                  {fmtTiempo(elapsed)}
                </span>
                {pendingUploads > 0 && (
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> subiendo audio...
                  </span>
                )}
                <div className="ml-auto">{controlesGrabacion}</div>
              </CardContent>
            </Card>
          )}

          {/* Buscador */}
          {!grabActiva && (
            <div className="mb-5 flex gap-2">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="buscador-input"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") ejecutarBusqueda(); }}
                  placeholder="Buscar palabra clave en transcripciones, acuerdos, tareas, temas..."
                  className="pl-9"
                />
              </div>
              <Button data-testid="buscar-btn" variant="outline" onClick={ejecutarBusqueda} disabled={buscando}>
                {buscando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                <span className="hidden md:inline md:ml-1">Buscar</span>
              </Button>
              {resultados !== null && (
                <Button data-testid="limpiar-busqueda-btn" variant="ghost" onClick={limpiarBusqueda}>
                  <X className="h-4 w-4 md:mr-1" /><span className="hidden md:inline">Limpiar</span>
                </Button>
              )}
            </div>
          )}

          {/* Lista */}
          {resultados !== null ? (
            <div className="space-y-3" data-testid="resultados-busqueda">
              <p className="text-sm text-slate-500">
                {resultados.length === 0
                  ? `Sin resultados para "${busqueda.trim()}"`
                  : `${resultados.length} ${resultados.length === 1 ? "reunión" : "reuniones"} con "${busqueda.trim()}"`}
              </p>
              {resultados.map((r) => (
                <Card key={r.id} data-testid={`resultado-${r.id}`}
                  className={`cursor-pointer hover:shadow-md transition-shadow ${r.es_mia ? "border-l-4 border-l-emerald-500" : "border-l-4 border-l-blue-400"}`}
                  onClick={() => abrirDetalle(r.id)}>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="font-semibold text-slate-900 dark:text-white">{r.titulo}</span>
                      {r.es_mia ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">Mía</Badge>
                      ) : (
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100">
                          <User className="h-3 w-3 mr-1" />{r.user_nombre}
                        </Badge>
                      )}
                      <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" />{fmtFecha(r.fecha_inicio)}</span>
                    </div>
                    <div className="space-y-1.5">
                      {r.coincidencias.map((c, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <Badge variant="outline" className="text-[10px] flex-shrink-0 mt-0.5">{c.fuente}</Badge>
                          <span className="text-slate-600 dark:text-slate-400">{resaltarTexto(c.fragmento, c.resaltar)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
          ) : items.length === 0 ? (
            <Card><CardContent className="py-16 text-center text-slate-500">
              <Mic className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No hay grabaciones todavía</p>
              <p className="text-sm">Presiona "Nueva Grabación" para grabar tu primera reunión</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3" data-testid="grabaciones-lista">
              {items.map((g) => {
                const est = ESTADO_LABEL[g.estado] || ESTADO_LABEL.procesando;
                const propia = g.es_mia;
                return (
                  <Card key={g.id} data-testid={`grabacion-item-${g.id}`}
                    className={propia ? "border-l-4 border-l-emerald-500" : "border-l-4 border-l-blue-400"}>
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-semibold text-slate-900 dark:text-white truncate">{g.titulo}</span>
                            {propia ? (
                              <Badge data-testid="badge-mia" className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">Mía</Badge>
                            ) : (
                              <Badge data-testid="badge-supervisado" className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100">
                                <User className="h-3 w-3 mr-1" />{g.user_nombre}
                              </Badge>
                            )}
                            <Badge variant="outline" className={est.cls}>
                              {EN_PROCESO.includes(g.estado) && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                              {g.estado === "completada" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                              {g.estado === "error" && <AlertTriangle className="h-3 w-3 mr-1" />}
                              {est.label}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtFecha(g.fecha_inicio)}</span>
                            <span>Duración: {fmtTiempo(g.duracion_segundos || 0)}</span>
                            <span className="flex items-center gap-1">
                              {(() => { const oi = ORIGEN_INFO[g.origen] || ORIGEN_INFO.pc; const OIcon = oi.Icon; return (<><OIcon className="h-3 w-3" />{oi.label}</>); })()}
                            </span>
                          </div>
                          {g.estado === "error" && g.error_detalle && (
                            <p className="text-xs text-red-600 mt-1">{g.error_detalle}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <Button data-testid={`ver-grabacion-${g.id}`} size="sm" variant="outline" onClick={() => abrirDetalle(g.id)}>
                            <Eye className="h-4 w-4 md:mr-1" /><span className="hidden md:inline">Ver</span>
                          </Button>
                          {g.estado === "completada" && (
                            <Button data-testid={`pdf-grabacion-${g.id}`} size="sm" variant="outline" onClick={() => descargarPdf(g)}>
                              <Download className="h-4 w-4 md:mr-1" /><span className="hidden md:inline">PDF</span>
                            </Button>
                          )}
                          {g.estado === "error" && (propia || esAdmin) && (
                            <Button data-testid={`reprocesar-grabacion-${g.id}`} size="sm" variant="outline" onClick={() => reprocesar(g)}>
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}
                          {(propia || esAdmin) && g.estado !== "grabando" && (
                            <Button data-testid={`eliminar-grabacion-${g.id}`} size="sm" variant="ghost"
                              className="text-red-500 hover:text-red-700" onClick={() => eliminar(g)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Overlay de grabación en móvil */}
      {grabActiva && isMobile && (
        <div className="fixed inset-0 z-50 bg-slate-950 flex flex-col items-center justify-between py-10 px-6" data-testid="grabacion-activa-movil">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-slate-400 text-sm mb-2">
              <Smartphone className="h-4 w-4" /> Grabando desde el celular
            </div>
            <p className="text-white font-semibold text-lg">
              {recState === "pausado" ? "En pausa" : recState === "finalizando" ? "Guardando reunión..." : "Reunión en curso"}
            </p>
          </div>
          <div className="flex flex-col items-center gap-6">
            <div className={`h-36 w-36 rounded-full flex items-center justify-center ${recState === "grabando" ? "bg-red-600/20 animate-pulse" : "bg-slate-800"}`}>
              <div className={`h-24 w-24 rounded-full flex items-center justify-center ${recState === "grabando" ? "bg-red-600" : "bg-slate-700"}`}>
                <Mic className="h-12 w-12 text-white" />
              </div>
            </div>
            <span data-testid="grabacion-timer-movil" className="font-mono text-5xl font-bold text-white tabular-nums">{fmtTiempo(elapsed)}</span>
            <canvas ref={canvasRef} width={260} height={50} />
            {pendingUploads > 0 && (
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> subiendo audio...
              </span>
            )}
            <p className="text-[11px] text-slate-500 text-center max-w-xs leading-relaxed">
              La pantalla se mantiene encendida mientras grabas. Si cambias de app la grabación continúa;
              evita bloquear el celular manualmente para no interrumpirla.
            </p>
          </div>
          <div className="flex items-center gap-8">{controlesGrabacion}</div>
        </div>
      )}

      {/* Dialog nueva grabación */}
      <Dialog open={showNuevaDialog} onOpenChange={setShowNuevaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mic className="h-5 w-5 text-red-600" /> Nueva Grabación</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!isMobile && (
              <div>
                <Label>Tipo de reunión</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button
                    type="button"
                    data-testid="modo-presencial-btn"
                    onClick={() => setModoGrabacion("mic")}
                    className={`border rounded-lg p-3 text-left text-sm transition-colors ${modoGrabacion === "mic" ? "border-red-500 bg-red-50 dark:bg-red-950/30" : "border-slate-200 dark:border-slate-700 hover:border-slate-300"}`}
                  >
                    <Mic className="h-4 w-4 mb-1 text-red-600" />
                    <span className="font-medium block">Presencial</span>
                    <span className="text-xs text-slate-500">Graba con el micrófono</span>
                  </button>
                  <button
                    type="button"
                    data-testid="modo-virtual-btn"
                    onClick={() => setModoGrabacion("virtual")}
                    className={`border rounded-lg p-3 text-left text-sm transition-colors ${modoGrabacion === "virtual" ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30" : "border-slate-200 dark:border-slate-700 hover:border-slate-300"}`}
                  >
                    <Video className="h-4 w-4 mb-1 text-blue-600" />
                    <span className="font-medium block">Virtual</span>
                    <span className="text-xs text-slate-500">Teams, Zoom, Meet...</span>
                  </button>
                </div>
              </div>
            )}
            <div>
              <Label>Título de la reunión (opcional)</Label>
              <Input data-testid="titulo-grabacion-input" value={tituloNueva} onChange={(e) => setTituloNueva(e.target.value)}
                placeholder="Ej: Reunión semanal de cobros" className="mt-1" />
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-900 rounded-lg p-3 space-y-1">
              {modoGrabacion === "virtual" && !isMobile ? (
                <>
                  <p className="flex items-center gap-1.5"><Video className="h-3.5 w-3.5" /> Únete primero a la reunión en Teams/Zoom/Meet.</p>
                  <p>• Al iniciar, elige la <b>pestaña o pantalla</b> de la reunión y marca la casilla <b>"Compartir audio"</b>.</p>
                  <p>• Se grabará el audio de la reunión junto con tu micrófono.</p>
                  <p>• Si detienes el compartir pantalla, la grabación se guarda automáticamente.</p>
                </>
              ) : (
                <>
                  <p className="flex items-center gap-1.5">{isMobile ? <Smartphone className="h-3.5 w-3.5" /> : <Monitor className="h-3.5 w-3.5" />} Se usará el micrófono de {isMobile ? "tu celular" : "tu PC"}.</p>
                  <p>• Puedes pausar y reanudar durante la reunión.</p>
                  <p>• La pantalla se mantendrá encendida mientras grabas.</p>
                </>
              )}
              <p>• Al finalizar se transcribe, se identifican los hablantes y se genera la minuta automáticamente.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNuevaDialog(false)}>Cancelar</Button>
            <Button data-testid="iniciar-grabacion-btn" onClick={iniciarGrabacion} className="bg-red-600 hover:bg-red-700 text-white">
              <Mic className="h-4 w-4 mr-2" /> Iniciar Grabación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog subir grabación existente */}
      <Dialog open={showSubirDialog} onOpenChange={(o) => { if (!subiendoArchivo) setShowSubirDialog(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-blue-600" /> Subir Grabación</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Título (opcional)</Label>
              <Input data-testid="titulo-subir-input" value={tituloSubir} onChange={(e) => setTituloSubir(e.target.value)}
                placeholder="Ej: Reunión Teams con aseguradora" className="mt-1" disabled={subiendoArchivo} />
            </div>
            <div>
              <Label>Archivo de audio o video</Label>
              <Input
                data-testid="archivo-subir-input"
                type="file"
                accept="audio/*,video/*,.mp4,.m4a,.mp3,.wav,.webm,.mov,.mkv"
                className="mt-1"
                disabled={subiendoArchivo}
                onChange={(e) => setArchivoSubir(e.target.files?.[0] || null)}
              />
              {archivoSubir && (
                <p className="text-xs text-slate-500 mt-1">
                  {archivoSubir.name} — {(archivoSubir.size / (1024 * 1024)).toFixed(1)} MB
                </p>
              )}
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-900 rounded-lg p-3 space-y-1">
              <p>• Sube la grabación que generó Teams, Zoom o Meet (MP4, M4A, MP3, WAV...). Máximo 2 GB.</p>
              <p>• El sistema extrae el audio, transcribe, identifica hablantes y genera la minuta.</p>
            </div>
            {subiendoArchivo && (
              <div>
                <div className="h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600 transition-all" style={{ width: `${uploadPct}%` }} />
                </div>
                <p className="text-xs text-slate-500 mt-1 text-center" data-testid="upload-progreso">Subiendo... {uploadPct}%</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubirDialog(false)} disabled={subiendoArchivo}>Cancelar</Button>
            <Button data-testid="confirmar-subir-btn" onClick={subirArchivo} disabled={!archivoSubir || subiendoArchivo} className="bg-blue-600 hover:bg-blue-700 text-white">
              {subiendoArchivo ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Subir y Procesar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog editar minuta */}
      <Dialog open={editOpen} onOpenChange={(o) => { if (!guardandoMinuta) setEditOpen(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-blue-600" /> Editar Minuta</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {editHablantes.length > 0 && (
              <div>
                <Label className="font-semibold">Nombres de hablantes</Label>
                <p className="text-xs text-slate-500 mb-2">Corrige los nombres; se actualizan en la transcripción, participantes y tareas.</p>
                <div className="space-y-2">
                  {editHablantes.map((h, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-sm text-slate-500 w-32 truncate flex-shrink-0">{h.original} →</span>
                      <Input
                        data-testid={`hablante-input-${i}`}
                        value={h.nuevo}
                        onChange={(e) => setEditHablantes((prev) => prev.map((x, j) => j === i ? { ...x, nuevo: e.target.value } : x))}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="font-semibold">Acuerdos Alcanzados</Label>
              <div className="space-y-2 mt-2">
                {editAcuerdos.map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      data-testid={`acuerdo-input-${i}`}
                      value={a}
                      onChange={(e) => setEditAcuerdos((prev) => prev.map((x, j) => j === i ? e.target.value : x))}
                    />
                    <Button variant="ghost" size="sm" className="text-red-500 flex-shrink-0 px-2"
                      onClick={() => setEditAcuerdos((prev) => prev.filter((_, j) => j !== i))}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button data-testid="agregar-acuerdo-btn" variant="outline" size="sm" onClick={() => setEditAcuerdos((prev) => [...prev, ""])}>
                  <Plus className="h-4 w-4 mr-1" /> Agregar acuerdo
                </Button>
              </div>
            </div>
            <div>
              <Label className="font-semibold">Tareas Asignadas</Label>
              <div className="space-y-2 mt-2">
                {editTareas.map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      data-testid={`tarea-desc-input-${i}`}
                      placeholder="Tarea"
                      value={t.descripcion}
                      onChange={(e) => setEditTareas((prev) => prev.map((x, j) => j === i ? { ...x, descripcion: e.target.value } : x))}
                    />
                    <Input
                      data-testid={`tarea-resp-input-${i}`}
                      placeholder="Responsable"
                      className="w-40 flex-shrink-0"
                      value={t.responsable}
                      onChange={(e) => setEditTareas((prev) => prev.map((x, j) => j === i ? { ...x, responsable: e.target.value } : x))}
                    />
                    <Button variant="ghost" size="sm" className="text-red-500 flex-shrink-0 px-2"
                      onClick={() => setEditTareas((prev) => prev.filter((_, j) => j !== i))}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button data-testid="agregar-tarea-btn" variant="outline" size="sm" onClick={() => setEditTareas((prev) => [...prev, { descripcion: "", responsable: "" }])}>
                  <Plus className="h-4 w-4 mr-1" /> Agregar tarea
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={guardandoMinuta}>Cancelar</Button>
            <Button data-testid="guardar-minuta-btn" onClick={guardarMinuta} disabled={guardandoMinuta} className="bg-blue-600 hover:bg-blue-700 text-white">
              {guardandoMinuta && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog ayuda permiso de micrófono */}
      <Dialog open={micAyudaOpen} onOpenChange={setMicAyudaOpen}>
        <DialogContent className="max-w-md" data-testid="mic-ayuda-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MicOff className="h-5 w-5 text-red-600" />
              {micErrorTipo === "sinmic" ? "No se detectó un micrófono" : micErrorTipo === "ocupado" ? "Micrófono ocupado" : "Activa el permiso del micrófono"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
            {micErrorTipo === "sinmic" ? (
              <p>No se encontró ningún micrófono conectado en este dispositivo. Conecta o activa un micrófono y presiona <b>Reintentar</b>.</p>
            ) : micErrorTipo === "ocupado" ? (
              <p>Otra aplicación está usando el micrófono (Teams, Zoom, otra pestaña...). Ciérrala o detén su uso del micrófono y presiona <b>Reintentar</b>.</p>
            ) : (
              <>
                <p>El navegador tiene <b>bloqueado el acceso al micrófono</b> para esta página. Actívalo así:</p>
                {(/iPad|iPhone|iPod/.test(navigator.userAgent)) ? (
                  <ol className="list-decimal ml-5 space-y-1.5">
                    <li>Toca el botón <b>"AA"</b> o el ícono de ajustes en la barra de dirección de Safari.</li>
                    <li>Entra a <b>Configuración del sitio web</b> → <b>Micrófono</b> → <b>Permitir</b>.</li>
                    <li>Si no aparece, ve a <b>Ajustes</b> del iPhone/iPad → <b>Apps</b> → <b>Safari</b> → <b>Micrófono</b> y actívalo.</li>
                  </ol>
                ) : isMobile ? (
                  <ol className="list-decimal ml-5 space-y-1.5">
                    <li>Toca el <b>candado 🔒</b> (o el ícono de ajustes) junto a la dirección de la página.</li>
                    <li>Entra a <b>Permisos</b> → <b>Micrófono</b> → <b>Permitir</b>.</li>
                    <li>Si no aparece: menú <b>⋮</b> de Chrome → <b>Configuración</b> → <b>Configuración de sitios</b> → <b>Micrófono</b>.</li>
                  </ol>
                ) : (
                  <ol className="list-decimal ml-5 space-y-1.5">
                    <li>Haz clic en el <b>candado 🔒</b> (o ícono de ajustes) a la izquierda de la dirección en la barra del navegador.</li>
                    <li>Busca <b>Micrófono</b> y cámbialo a <b>Permitir</b>.</li>
                    <li>Si tu sistema lo bloquea: en Windows ve a <b>Configuración → Privacidad → Micrófono</b> y permite el acceso al navegador.</li>
                  </ol>
                )}
                <p>Luego presiona <b>Reintentar</b>. Si el navegador te pregunta, elige <b>Permitir</b>.</p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMicAyudaOpen(false)}>Cerrar</Button>
            <Button data-testid="mic-reintentar-btn" className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => { setMicAyudaOpen(false); setTimeout(() => iniciarGrabacion(), 300); }}>
              <Mic className="h-4 w-4 mr-2" /> Reintentar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog detalle */}
      <Dialog open={!!detalle || detalleLoading} onOpenChange={(o) => { if (!o) { setDetalle(null); if (audioUrl) URL.revokeObjectURL(audioUrl); setAudioUrl(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detalleLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
          ) : detalle && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2 pr-6">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <span className="truncate">{detalle.titulo}</span>
                  {detalle.es_mia
                    ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">Mía</Badge>
                    : <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100">{detalle.user_nombre}</Badge>}
                </DialogTitle>
              </DialogHeader>
              <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1 -mt-2">
                <span>{fmtFecha(detalle.fecha_inicio)}</span>
                <span>Duración: {fmtTiempo(detalle.duracion_segundos || 0)}</span>
              </div>
              {EN_PROCESO.includes(detalle.estado) && (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {ESTADO_LABEL[detalle.estado]?.label}... esto puede tardar unos minutos. Puedes cerrar y volver luego.
                </div>
              )}
              {detalle.estado === "error" && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                  <AlertTriangle className="h-4 w-4 inline mr-1" /> {detalle.error_detalle || "Error en el procesamiento"}
                </div>
              )}
              {detalle.estado === "completada" && (
                <Tabs defaultValue="minuta">
                  <TabsList>
                    <TabsTrigger value="minuta" data-testid="tab-minuta"><ListChecks className="h-4 w-4 mr-1" /> Minuta</TabsTrigger>
                    <TabsTrigger value="transcripcion" data-testid="tab-transcripcion"><FileText className="h-4 w-4 mr-1" /> Transcripción</TabsTrigger>
                    <TabsTrigger value="audio" data-testid="tab-audio"><Play className="h-4 w-4 mr-1" /> Audio</TabsTrigger>
                  </TabsList>
                  <TabsContent value="minuta" className="pt-3">
                    {detalle.minuta ? renderMinuta(detalle.minuta) : <p className="text-sm text-slate-500">Sin minuta</p>}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button data-testid="descargar-pdf-detalle-btn" onClick={() => descargarPdf(detalle)} variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-1" /> Descargar Minuta en PDF
                      </Button>
                      {(detalle.es_mia || esAdmin) && (
                        <Button data-testid="editar-minuta-btn" onClick={abrirEditorMinuta} variant="outline" size="sm">
                          <Pencil className="h-4 w-4 mr-1" /> Editar Minuta
                        </Button>
                      )}
                    </div>
                  </TabsContent>
                  <TabsContent value="transcripcion" className="pt-3">
                    {detalle.transcripcion_hablantes ? (
                      <>
                        <p className="text-xs text-violet-600 dark:text-violet-400 mb-2">
                          Hablantes identificados por IA a partir del contexto (aproximado)
                        </p>
                        <div data-testid="transcripcion-contenido" className="text-sm text-slate-700 dark:text-slate-300 max-h-96 overflow-y-auto bg-slate-50 dark:bg-slate-900 rounded-lg p-4 space-y-2">
                          {detalle.transcripcion_hablantes.split("\n").filter((l) => l.trim()).map((linea, i) => {
                            const m = linea.match(/^([^:]{1,40}):\s*(.*)$/);
                            return m ? (
                              <p key={i}><span className="font-semibold text-violet-700 dark:text-violet-400">{m[1]}:</span> {m[2]}</p>
                            ) : (
                              <p key={i}>{linea}</p>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div data-testid="transcripcion-contenido" className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap max-h-96 overflow-y-auto bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
                        {detalle.transcripcion || "Sin transcripción"}
                      </div>
                    )}
                  </TabsContent>
                  <TabsContent value="audio" className="pt-3 space-y-3">
                    {!audioUrl ? (
                      <Button data-testid="cargar-audio-btn" variant="outline" onClick={() => cargarAudio(0)} disabled={audioLoading}>
                        {audioLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                        Reproducir grabación
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        <audio ref={audioElRef} src={audioUrl} controls className="w-full" onEnded={onAudioEnded} data-testid="audio-player" />
                        {(detalle.segmentos || 0) > 1 && (
                          <p className="text-xs text-slate-500">
                            Parte {audioIdx + 1} de {detalle.segmentos} — se reproduce automáticamente la siguiente.
                          </p>
                        )}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
